import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  FileEntityLink,
  StorageService as TaggedStorageService,
} from '../services/storage.service';

export interface AttachmentFile {
  object_key: string;
  name: string;
  size?: number;
  last_modified?: string;
  mime_type?: string;
}

export interface AttachmentUploadSummary {
  successCount: number;
  failureCount: number;
  failedFiles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class CommentsAttachmentsService {
  constructor(private storageService: TaggedStorageService) {}

  uploadCommentAttachments(
    commentId: number,
    files: File[],
    baseTags: string[] = []
  ): Observable<AttachmentUploadSummary> {
    if (!files.length) {
      return of({ successCount: 0, failureCount: 0, failedFiles: [] });
    }

    const auth = this.getAuthContext();
    if (!auth) {
      return of({
        successCount: 0,
        failureCount: files.length,
        failedFiles: files.map((file) => file.name),
      });
    }

    const tags = this.buildTags([...baseTags, `comment:${commentId}`]);
    const links = this.buildLinks([...baseTags, `comment:${commentId}`]);
    const uploads = files.map((file, index) => {
      const objectKey = `comment/${commentId}/${Date.now()}_${index}_${this.normalizeFileName(file.name)}`;
      return this.storageService
        .uploadFile(file, objectKey, auth.companyId, auth.token, tags, links)
        .pipe(
          map(() => ({ success: true, fileName: file.name })),
          catchError(() => of({ success: false, fileName: file.name }))
        );
    });

    return forkJoin(uploads).pipe(
      map((results) => {
        const failedFiles = results.filter((r) => !r.success).map((r) => r.fileName);
        return {
          successCount: results.length - failedFiles.length,
          failureCount: failedFiles.length,
          failedFiles,
        };
      })
    );
  }

  uploadTaskAttachments(
    taskId: number,
    files: File[],
    baseTags: string[] = []
  ): Observable<AttachmentUploadSummary> {
    if (!files.length) {
      return of({ successCount: 0, failureCount: 0, failedFiles: [] });
    }

    const auth = this.getAuthContext();
    if (!auth) {
      return of({
        successCount: 0,
        failureCount: files.length,
        failedFiles: files.map((file) => file.name),
      });
    }

    const tags = this.buildTags([...baseTags, `task:${taskId}`]);
    const links = this.buildLinks([...baseTags, `task:${taskId}`]);
    const uploads = files.map((file, index) => {
      const objectKey = `tasks/${taskId}/${Date.now()}_${index}_${this.normalizeFileName(file.name)}`;
      return this.storageService
        .uploadFile(file, objectKey, auth.companyId, auth.token, tags, links)
        .pipe(
          map(() => ({ success: true, fileName: file.name })),
          catchError(() => of({ success: false, fileName: file.name }))
        );
    });

    return forkJoin(uploads).pipe(
      map((results) => {
        const failedFiles = results.filter((r) => !r.success).map((r) => r.fileName);
        return {
          successCount: results.length - failedFiles.length,
          failureCount: failedFiles.length,
          failedFiles,
        };
      })
    );
  }

  listCommentAttachments(commentId: number): Observable<AttachmentFile[]> {
    return this.listByPrefix(`comment/${commentId}/`);
  }

  listTaskAttachments(taskId: number): Observable<AttachmentFile[]> {
    const auth = this.getAuthContext();
    if (!auth) {
      return of([]);
    }
    return this.storageService
      .listFilesByEntity('task', String(taskId), auth.companyId, auth.token, true, true)
      .pipe(
        map((response: any) => {
          const files = response?.files || [];
          return files.map((file: any) => ({
            object_key: file.object_key || file.key || '',
            name: file.name || (file.object_key || file.key || '').split('/').pop() || 'Attachment',
            size: file.size,
            last_modified: file.last_modified,
            mime_type: file.mime_type,
          }));
        }),
        catchError(() => of([]))
      );
  }

  getDownloadUrl(objectKey: string): Observable<string> {
    const auth = this.getAuthContext();
    if (!auth) {
      return of('');
    }
    return this.storageService.getPresignedDownloadUrl(objectKey, auth.companyId, auth.token).pipe(
      map((response: any) => {
        const rawUrl = response?.url || response?.presigned_url || '';
        if (!rawUrl) return '';
        return rawUrl.startsWith('http://') ? rawUrl.replace('http://', 'https://') : rawUrl;
      }),
      catchError(() => of(''))
    );
  }

  private listByPrefix(prefix: string): Observable<AttachmentFile[]> {
    const auth = this.getAuthContext();
    if (!auth) {
      return of([]);
    }

    return this.storageService.listFiles(auth.companyId, auth.token, prefix, false).pipe(
      map((response: any) => {
        const files = response?.files || [];
        return files.map((file: any) => ({
          object_key: file.object_key || file.key || '',
          name: file.name || (file.object_key || file.key || '').split('/').pop() || 'Attachment',
          size: file.size,
          last_modified: file.last_modified,
          mime_type: file.mime_type,
        }));
      }),
      catchError(() => of([]))
    );
  }

  private buildTags(tags: string[]): string {
    const deduped = Array.from(
      new Set(
        tags
          .map((tag) => tag.trim())
          .filter((tag) => !!tag && tag.includes(':'))
      )
    );
    return deduped.join(',');
  }

  private normalizeFileName(name: string): string {
    return name.replace(/\s+/g, '_');
  }

  private buildLinks(tags: string[]): FileEntityLink[] {
    const links: FileEntityLink[] = [];
    tags.forEach((tag) => {
      const clean = tag.trim();
      if (!clean || !clean.includes(':')) return;
      const [entityType, ...rest] = clean.split(':');
      const entityId = rest.join(':').trim();
      if (!entityType || !entityId) return;
      links.push({
        entity_type: entityType.trim().toLowerCase(),
        entity_id: entityId,
        link_type: 'related',
      });
    });
    const seen = new Set<string>();
    return links.filter((link) => {
      const key = `${link.entity_type}:${link.entity_id}:${link.link_type || 'related'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private getAuthContext(): { companyId: string; token: string } | null {
    try {
      const rawUser = localStorage.getItem('optplanner_user');
      const user = rawUser ? JSON.parse(rawUser) : {};
      const companyId = user?.companyId || user?.company_id || '';
      const token = localStorage.getItem('optplanner_token') || user?.token || '';
      if (!companyId || !token) {
        return null;
      }
      return { companyId, token };
    } catch {
      return null;
    }
  }
}
